import { useState, useEffect } from 'react';
import { Option } from 'src/types';
import { pick } from 'lodash-es';
import { useTranslations } from 'next-intl';
import { OptionBadge } from './OptionCard';
import { Carousel } from 'react-bootstrap';
import { FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { take } from 'lodash-es';
import ImageWithFallback from './ImageWithFallback';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import ApiClient from 'src/lib/api_client';

const OptionCardEdit: React.FC<{
  option: Option;
  colClass?: string;
  isGift?: boolean;
  onChange: (value: { [key in 'name' | 'subtitle']?: string | undefined }) => void;
}> = ({ option, colClass, isGift, onChange }) => {
  const translation = useTranslations('components.OptionCardEdit');
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const generateOptionSubtitles = useEnhanceApiClient(ApiClient.generateOptionSubtitles);
  const [fields, setFields] = useState(pick(option, ['name', 'subtitle']));
  const optionName = `${isGift ? `[${translation('gift')}] ` : ''}${option.name}`;
  useEffect(() => {
    setFields(pick(option, ['name', 'subtitle']));
  }, [option.name, option.subtitle]);

  const handleGenerateSubtitle = async () => {
    setIsGenerating(true);
    setError('');

    try {
      const [json] = await generateOptionSubtitles({
        optionIds: [option.id],
      });

      if ('error' in json && json.error) {
        setError(json.error);
        return;
      }

      const subtitles = json as Record<number, string | undefined>;
      const generatedSubtitle = subtitles[option.id];

      onChange({ subtitle: generatedSubtitle || option.subtitle });
      setFields((prev) => ({ ...prev, subtitle: generatedSubtitle || option.subtitle }));
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={`${colClass || 'col-md-4'} tw-mb-12 md:tw-mb-8`}>
      <OptionBadge option={option} />

      <div className="carousel-activity-sao-image-container">
        <Carousel
          controls={option.images.length > 1}
          prevIcon={
            <div className="carousel-activity-sao-image__btn-inner">
              <FiChevronLeft strokeWidth={2} className="tw-text-brand-navy" />
            </div>
          }
          nextIcon={
            <div className="carousel-activity-sao-image__btn-inner">
              <FiChevronRight strokeWidth={2} className="tw-text-brand-navy" />
            </div>
          }
          indicators={option.images.length > 1}
          className="carousel-activity-sao-image tw-mb-3 carousel slide"
          interval={null}
          pause={false}
          keyboard={false}
          touch={false}
        >
          {option.images.length > 0 ? (
            take(option.images, 8).map((image) => (
              <Carousel.Item key={image.id} className="carousel-item tw-relative">
                <ImageWithFallback
                  record={image}
                  imgField="webpMdUrl"
                  fill
                  alt={image.caption || optionName}
                  className="tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg tw-object-cover tw-cursor-pointer"
                />
              </Carousel.Item>
            ))
          ) : (
            <Carousel.Item className="carousel-item tw-relative">
              <ImageWithFallback
                src={undefined}
                fill
                alt={optionName}
                className="tw-rounded-lg tw-object-cover tw-cursor-pointer"
              />
            </Carousel.Item>
          )}
        </Carousel>
      </div>

      <div>
        <div>
          <div className="tw-text-2xs tw-text-grey-dark tw-text-right">
            {(fields.name || '').length}/70
          </div>
          <textarea
            value={fields.name}
            maxLength={70}
            className="form-control tw-mb-2 tw-text-sm"
            onChange={(e) => {
              onChange({ name: e.target.value });
              setFields((prev) => ({ ...prev, name: e.target.value }));
            }}
          />
        </div>
        <div>
          <div className="tw-text-2xs tw-text-grey-dark tw-text-right">
            {(fields.subtitle || '').length}/100
          </div>
          <textarea
            value={fields.subtitle}
            maxLength={100}
            className="form-control tw-mb-2 tw-text-sm"
            rows={3}
            onChange={(e) => {
              onChange({ subtitle: e.target.value });
              setFields((prev) => ({ ...prev, subtitle: e.target.value }));
            }}
          />
        </div>
        <div className="tw-text-sm tw-text-orange tw-underline tw-cursor-pointer">
          <div onClick={handleGenerateSubtitle}>
            {isGenerating ? (
              <div className="spinner-border spinner-border-sm tw-text-orange tw-mr-2"></div>
            ) : null}
            {translation('generateWithAI')}
          </div>
        </div>
        {error ? <div className="tw-text-red tw-text-sm tw-mb-2">{error}</div> : null}
      </div>
    </div>
  );
};

export default OptionCardEdit;
